import { $$ } from './HelperFunctions'

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

export class Lightbox {
  constructor (selector = '[data-js-lightbox]') {
    this.selector = selector
    this.triggers = []
    this.overlay = null
    this.image = null
    this.closeButton = null
    this.lastFocused = null
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this.onKeydown = this.onKeydown.bind(this)
    this.close = this.close.bind(this)
  }

  init () {
    this.triggers = $$(this.selector)

    if (!this.triggers.length) {
      return
    }

    this.buildOverlay()

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault()
        this.open(trigger)
      })
    })
  }

  buildOverlay () {
    const overlay = document.createElement('div')
    overlay.className = 'lightbox'
    overlay.setAttribute('role', 'dialog')
    overlay.setAttribute('aria-modal', 'true')
    overlay.setAttribute('aria-label', 'Bildansicht')
    overlay.hidden = true

    const figure = document.createElement('figure')
    figure.className = 'lightbox__figure'

    const image = document.createElement('img')
    image.className = 'lightbox__image'
    image.alt = ''

    const closeButton = document.createElement('button')
    closeButton.type = 'button'
    closeButton.className = 'lightbox__close'
    closeButton.setAttribute('aria-label', 'Ansicht schließen')

    figure.appendChild(image)
    overlay.appendChild(figure)
    overlay.appendChild(closeButton)
    document.body.appendChild(overlay)

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay || event.target === figure) {
        this.close()
      }
    })

    closeButton.addEventListener('click', this.close)

    this.overlay = overlay
    this.image = image
    this.closeButton = closeButton
  }

  open (trigger) {
    const source = trigger.dataset.jsLightboxSrc
    const triggerImage = trigger.querySelector('img')

    if (!source && !triggerImage) {
      return
    }

    this.lastFocused = document.activeElement
    this.image.src = source || triggerImage.currentSrc || triggerImage.src
    this.image.alt = trigger.dataset.jsLightboxAlt || (triggerImage ? triggerImage.alt : '')

    this.overlay.hidden = false
    document.documentElement.classList.add('is-lightbox-open')

    window.requestAnimationFrame(() => {
      this.overlay.classList.add('is-visible')
    })

    document.addEventListener('keydown', this.onKeydown)
    this.closeButton.focus()
  }

  close () {
    if (this.overlay.hidden) {
      return
    }

    document.removeEventListener('keydown', this.onKeydown)
    document.documentElement.classList.remove('is-lightbox-open')
    this.overlay.classList.remove('is-visible')

    const hide = () => {
      if (this.overlay.classList.contains('is-visible')) {
        return
      }

      this.overlay.hidden = true
      this.image.removeAttribute('src')
    }

    if (this.reducedMotion) {
      hide()
    } else {
      this.overlay.addEventListener('transitionend', hide, { once: true })
    }

    if (this.lastFocused) {
      this.lastFocused.focus()
    }
  }

  onKeydown (event) {
    if (event.key === 'Escape') {
      this.close()
      return
    }

    if (event.key === 'Tab') {
      this.trapFocus(event)
    }
  }

  trapFocus (event) {
    const focusables = $$(FOCUSABLE, this.overlay).filter((element) => !element.hidden)

    if (!focusables.length) {
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}

export default Lightbox
